import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Callout } from '@/shared/ui';
import { useSetupReadiness } from '../hooks/useSetupReadiness';
import type { SetupReadiness } from '../api/setupApi';

const HARD_GATE_LABELS: Record<keyof SetupReadiness['hardGates'], string> = {
  legalNamePresent: 'Legal / billing entity name',
  tinValid: 'Tax ID (TIN)',
  groupNpiValid: 'Group NPI',
  hasProviderWithValidNpi: 'At least one provider with a valid NPI',
  licensesCompleteWhereRequired: 'Provider license number + state (required by a selected payer)',
  hasAtLeastOnePayer: 'At least one payer added',
};

// Non-blocking status summary — surfaces PRD §6's readiness gate + soft
// states on both the Practice Credentials and Payers pages. Nothing in this
// build actually blocks on it (no "New Batch" gate exists yet).
export function SetupReadinessBanner() {
  const { readiness, isLoading } = useSetupReadiness();

  if (isLoading || !readiness) return null;

  const missing = (
    Object.keys(readiness.hardGates) as Array<keyof SetupReadiness['hardGates']>
  ).filter((key) => !readiness.hardGates[key]);
  const uncharted = readiness.softStates.payersWithoutActiveMap;

  if (missing.length === 0 && uncharted.length === 0) return null;

  return (
    <Callout
      icon={missing.length > 0 ? AlertTriangle : CheckCircle2}
      variant={missing.length > 0 ? 'warning' : 'info'}
      title={missing.length > 0 ? 'Setup incomplete' : 'Some payers are staff-routed'}
    >
      {missing.length > 0 && (
        <div>Missing: {missing.map((key) => HARD_GATE_LABELS[key]).join(', ')}.</div>
      )}
      {uncharted.length > 0 && <div>Routing to staff until charted: {uncharted.join(', ')}.</div>}
    </Callout>
  );
}
